import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { IcClipboardList, IcSearch, IcClose, IcChevronRight } from '../../theme/icons';
import Swal from '../../theme/swal';
import { format } from 'date-fns';
import commandeService from '../../services/commandeService';
import CommandeTabs from '../../components/CommandeTabs';
import { matchesSearch } from '../../utils/search';

const fmt = (v) => `${(parseFloat(v) || 0).toFixed(3)} DT`;

// Livre §04 bon de commande : « ✎ Brouillon » gris · Envoyé texte vert
// #16A34A · Reçu texte vert #15803D · Annulé texte gris #4B5563 (sans icône).
export const STATUS_META = {
  BROUILLON: { label: '✎ Brouillon', icon: null, cls: 'bdg-neutral' },
  ENVOYE: { label: 'Envoyé', icon: null, cls: 'bdg-wait' },
  RECU: { label: 'Reçu', icon: null, cls: 'bdg-arrive' },
  ANNULE: { label: 'Annulé', icon: null, cls: 'bdg-off' },
};

export const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.BROUILLON;
  const Icon = meta.icon;
  return (
    <span className={`bdg ${meta.cls}`}>
      {Icon && <Icon size={12} />}
      {meta.label}
    </span>
  );
};

// `status` comes from the route (BROUILLON | ENVOYE | RECU) — one tab per statut.
const CommandesList = ({ status = 'BROUILLON' }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await commandeService.getAllOrders();
      setOrders(data);
    } catch (e) {
      console.error('Erreur chargement bons de commande:', e);
      Swal.fire('Erreur', 'Impossible de charger les bons de commande', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    // Règle Ki Para (utils/search.js) : fournisseur par début de mot, n° « contient ».
    return orders.filter((o) => o.status === status && matchesSearch(search, o.supplier_name, [o.number]));
  }, [orders, status, search]);

  return (
    <div className="w-full px-4 pt-2 pb-4">
      <CommandeTabs />

      {/* Titre « Bons de commande » + sous-titre retirés (owner Studio 2026-08-13). */}

      {/* Recherche — même design/place que la barre de « Nouvelle commande ». */}
      <div className="flex gap-3 mb-3 flex-shrink-0">
        <div className="relative flex-1">
          <IcSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher (n° ou fournisseur)..."
            className="w-full pl-11 pr-9 py-2.5 border border-white rounded-xl focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 bg-white"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <IcClose />
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        {loading && orders.length === 0 ? (
          <div className="p-10 text-center text-gray-500">Chargement…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <IcClipboardList className="mx-auto text-4xl text-gray-300 mb-3" />
            <p className="text-gray-600 font-medium">Aucun bon de commande</p>
            <p className="text-sm text-gray-400 mt-1">
              Créez-en depuis la page{' '}
              <Link to="/commandes/a-commander" className="text-gray-700 hover:bg-gray-100 hover:text-primary">Nouvelle commande</Link>.
            </p>
          </div>
        ) : (
          /* Tableau dense (Composants-2 §6) : en-tête th-ki 8/12px, lignes
             13px 8/12px, nom 600, montant à droite tabulaire 600, survol
             gray-100, chevron › gray-400 (lignes cliquables). */
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left uppercase">
                  {/* Mobile : seulement N° / Fournisseur / Articles / Total à
                      payer — pas de défilement latéral. Desktop : tout. */}
                  <th className="th-ki px-2 md:px-4 py-3">N°</th>
                  <th className="th-ki hidden md:table-cell px-4 py-3">Date</th>
                  <th className="th-ki px-2 md:px-4 py-3">Fournisseur</th>
                  <th className="th-ki px-2 md:px-4 py-3 text-center">Articles</th>
                  <th className="th-ki px-2 md:px-4 py-3 text-right">
                    <span className="md:hidden">Total à payer</span>
                    <span className="hidden md:inline">Total estimé</span>
                  </th>
                  <th className="th-ki hidden md:table-cell px-4 py-3 text-center">Statut</th>
                  <th className="th-ki hidden md:table-cell px-4 py-3 w-8" aria-label="Ouvrir" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((o) => (
                  <tr
                    key={o.id}
                    onClick={() => navigate(`/commandes/${o.id}`)}
                    className="border-b border-gray-100 last:border-b-0 tr-ki transition-colors"
                  >
                    <td className="td-ki px-2 md:px-4 py-3 font-semibold text-gray-900 tabular-nums whitespace-nowrap">BC #{o.number}</td>
                    {/* Liste Envoyés : « Arrivée prévue » EN PREMIER (noir gras),
                        la date du bon juste en dessous (choix Ahmed 2026-08-20).
                        Les autres listes n'affichent que la date du bon. */}
                    <td className="td-ki hidden md:table-cell px-4 py-3 text-gray-600 whitespace-nowrap">
                      {status === 'ENVOYE' && o.expected_arrival_date && (
                        <div className="text-gray-900 font-bold">
                          Arrivée prévue {format(new Date(o.expected_arrival_date), 'dd/MM/yyyy')}
                        </div>
                      )}
                      <div className="text-xs tabular-nums">
                        {o.date_added ? format(new Date(o.date_added), 'dd/MM/yyyy HH:mm') : '—'}
                      </div>
                    </td>
                    <td className="td-ki px-2 md:px-4 py-3">
                      <div className="font-semibold text-gray-900">{o.supplier_name}</div>
                      {o.supplier_phone && (
                        <div className="text-xs text-gray-400 tabular-nums">{o.supplier_phone}</div>
                      )}
                    </td>
                    <td className="td-ki px-2 md:px-4 py-3 text-center tabular-nums">
                      {(o.items || []).length} <span className="text-gray-400">({o.items_count} u.)</span>
                    </td>
                    <td className="td-ki px-2 md:px-4 py-3 text-right font-semibold tabular-nums whitespace-nowrap">
                      {(parseFloat(o.expected_total) || 0).toFixed(3)}
                      <span className="hidden md:inline"> DT</span>
                    </td>
                    <td className="td-ki hidden md:table-cell px-4 py-3 text-center">
                      {/* Liste allégée (choix Ahmed 2026-08-20) : plus de pastille
                          « Sans facture » ni de note ici — l'info reste sur la
                          fiche du bon. */}
                      <StatusBadge status={o.status} />
                    </td>
                    <td className="td-ki hidden md:table-cell px-4 py-3 text-right text-gray-400">
                      <IcChevronRight size={14} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommandesList;
